import type { YouTubeVideo } from '@kidshub/contracts';
import { useTranslation } from 'react-i18next';
import { splitDuration } from '../lib/duration.js';
import { Badge } from '../ui/kit.js';
import './import.css';

/**
 * The step that cannot be skipped: the parent watches it first.
 *
 * `SPEC.md` §11.1 asks for this because of a constraint rather than in spite of
 * one. There is no reliable way to ask the API whether a video carries
 * advertising, so the only honest answer is to let the parent look. It turns an
 * unavoidable weakness into the product's own curation step, and it is the
 * reason the middle of this flow is not negotiable.
 *
 * The Parent App may use the embedded player: it is an adult surface and not
 * child-directed. The privacy-enhanced host is used here as it is on the
 * tablet, and the player is left alone — not covered, not overlaid, not
 * scripted (§11.2).
 *
 * The §11.3 disclosure is not part of this component: the page places it beside
 * the button that moves on, where it is read by someone about to decide.
 */

interface YouTubePreviewProps {
  video: YouTubeVideo;
}

const EMBED_HOST = 'https://www.youtube-nocookie.com/embed';

export const YouTubePreview = ({ video }: YouTubePreviewProps) => {
  const { t } = useTranslation();
  const { hours, minutes } = splitDuration(video.durationSeconds ?? 0);

  return (
    <div className="yt-preview">
      <div className="yt-player">
        <iframe
          title={video.title}
          src={`${EMBED_HOST}/${video.videoId}`}
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>

      <div>
        <h3 className="t-title">{video.title}</h3>
        <p className="t-meta">{video.channelTitle}</p>
      </div>

      <div className="chips">
        {video.durationSeconds === null ? null : (
          <Badge tone="line">{t('youtube.duration', { hours, minutes })}</Badge>
        )}
        {video.isMadeForKids ? <Badge tone="sage">{t('youtube.madeForKids')}</Badge> : null}
      </div>

      {video.isMadeForKids ? <p className="help">{t('youtube.madeForKidsNote')}</p> : null}
    </div>
  );
};
